/**
 * Saved reports and dashboards as the kernel keeps them (LO-217), with no React in it.
 *
 * The kernel holds a report as a question (`spec`, checked by the same parser
 * that answers a query), the form it was built from (`state`) and who may open
 * it. This turns the app's own shapes into that and back. It never carries an
 * answer, a tag or a grant, and it does not decide who may open anything: that
 * is the kernel's list on the record.
 */
import { buildSpec } from "./spec.js";

/** What the kernel is sent to save one chart. */
export function reportBody(state, schema) {
  return { spec: buildSpec(state, schema), state, chart: state.chart || "auto" };
}

/** What the kernel is sent to save a dashboard: each chart's spec and form, and what is shared across them. */
export function dashboardBody(dashboard, schema) {
  return {
    items: dashboard.items.map((i) => ({
      id: i.id,
      title: i.title,
      spec: buildSpec(i.state, schema),
      state: i.state,
      chart: i.state.chart || "auto",
      ...(i.sourceId ? { sourceId: i.sourceId } : {}),
    })),
    filters: (dashboard.filters || []).map((f) => ({ field: f.field, op: f.op, value: f.value ?? "" })),
    range: { from: dashboard.range?.from || "", to: dashboard.range?.to || "" },
  };
}

/** A kernel report of kind `report`, as the saved list and the builder hold one. */
export function savedFrom(report) {
  return {
    id: report.id,
    name: report.name,
    // The form, plus the two things a spec can say that the form cannot (LO-227), so opening it keeps them.
    state: report.body?.state
      ? { ...report.body.state,
          ...(report.body.spec?.source ? { source: report.body.spec.source } : {}),
          ...(report.body.spec?.linked ? { linked: report.body.spec.linked } : {}) }
      : report.body?.state,
    role: report.role,
    owner: report.owner,
    version: report.version,
    updatedAt: report.updated_at,
    sharedUsers: report.shared_users || [],
    sharedGroups: report.shared_groups || [],
    // Only the owner is told, and only when there is one.
    schedule: report.schedule || null,
    alerts: report.alerts || [],
  };
}

/**
 * A kernel report of kind `dashboard`, as the dashboards screen holds one. A
 * viewer's is read-only: they can narrow it for themselves and nothing is saved.
 */
export function dashboardFrom(report) {
  return {
    id: report.id,
    name: report.name,
    readOnly: report.role !== "owner",
    shared: true,
    owner: report.owner,
    role: report.role,
    version: report.version,
    sharedUsers: report.shared_users || [],
    sharedGroups: report.shared_groups || [],
    items: (report.body?.items || []).map(({ id, title, state, sourceId }) => ({
      id, title, state, ...(sourceId ? { sourceId } : {}),
    })),
    filters: report.body?.filters || [],
    range: { from: report.body?.range?.from || "", to: report.body?.range?.to || "" },
  };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Who can open it, as the owner reads it. A viewer is told only who shared it. */
export function sharingLine(item) {
  if (item.role === "viewer") return `Shared with you by ${item.owner}`;
  const people = item.sharedUsers?.length || 0;
  const groups = item.sharedGroups?.length || 0;
  if (!people && !groups) return "Only you";
  return `Shared with ${[people ? plural(people, "person", "people") : "", groups ? plural(groups, "group", "groups") : ""]
    .filter(Boolean).join(" and ")}`;
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** When a report is emailed, as the kernel words it: `Emailed every Monday at 09:00 UTC to 2 people`. */
export function scheduleLine(schedule) {
  if (!schedule) return "";
  const hour = `${String(schedule.hour).padStart(2, "0")}:00 UTC`;
  const when = schedule.cadence === "weekly" ? `every ${WEEKDAYS[schedule.weekday ?? 0]} at ${hour}`
    : schedule.cadence === "monthly" ? `on day ${schedule.day} of each month at ${hour}`
    : `every day at ${hour}`;
  const n = schedule.recipients?.length || 0;
  return `Emailed ${when} to ${plural(n, "person", "people")}`;
}

export const ALERT_OPS = { gt: "above", gte: "at or above", lt: "below", lte: "at or below" };

/** `Tell 2 people when Count is above 100`, as the kernel words it. */
export function alertLine(alert, label = "the figure") {
  if (!alert) return "";
  const n = alert.recipients?.length || 0;
  const t = Number(alert.threshold);
  const shown = Number.isInteger(t) ? t.toLocaleString("en-US") : t.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return `Tell ${plural(n, "person", "people")} when ${label} is ${ALERT_OPS[alert.op]} ${shown}`;
}

/** What an edit failure should say, in the kernel's words where it has some. */
export function failureText(error) {
  if (error?.error === "version_conflict") {
    return "Somebody else changed this since you opened it. It has been reloaded; make your change again.";
  }
  if (error?.error === "unknown_user" || error?.error === "unknown_group") return error.message;
  return error?.message || "The kernel could not do that.";
}

/**
 * What is still only in this browser from before reports lived in the kernel:
 * saved charts and dashboards that have not been moved. Nothing is removed from
 * the browser until the kernel has them.
 */
export function movable(saved, dashboards) {
  return { saved: saved || [], dashboards: dashboards || [], count: (saved?.length || 0) + (dashboards?.length || 0) };
}
